import { Inline, vars } from '@etg/wings';
import { ReactComponent as RevolutPlus } from '@eti/assets/icons/revolut/plus.svg';
import { ReactComponent as RevolutMinus } from '@eti/assets/icons/revolut/minus.svg';
import { useRevolut, useProperty, useTranslation } from '@eti/providers';
import { css, cx, resetButtonStyle } from '@eti/styles';
import { constants } from '@eti/utils';
import { Minus, MinusCircle, Plus, PlusCircle } from '@phosphor-icons/react';
import { screenReadersOnlyStyles } from '../../../../utils/styleUtils';

const { REVOLUT } = constants.brands;

const getRevolutButtonStyles = (isDisabled = false) => css`
  align-items: center;
  background-color: ${isDisabled ? '#f0f0f0' : '#eee'};
  border-radius: ${vars.inputs.borderRadius};
  color: ${isDisabled ? '#8c8c8c' : '#282828'};
  cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
  display: flex;
  height: 2.5rem;
  justify-content: center;
  width: 2.75rem;

  @media (prefers-color-scheme: dark) {
    background-color: ${isDisabled ? '#4a4a4a' : '#545454'};
    color: ${isDisabled ? '#a4a4a4' : vars.colors.text.primary};
  }
`;

const positioningStyles = css`
  align-items: center;
  height: 100%;
`;

const getButtonStyles = (isDisabled = false) => css`
  color: ${isDisabled ? vars.colors.inputs.disabledBorder : vars.colors.links.default};
  cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
  display: flex;
  position: relative;

  &::after {
    border-color: ${vars.colors.focusRing};
    border-radius: 50%;
    border-style: solid;
    border-width: 0.125rem;
    content: '';
    height: 1.75rem;
    left: -0.125rem;
    opacity: 0;
    position: absolute;
    top: -0.125rem;
    transition: opacity 0.2s ease-in-out;
    width: 1.75rem;
  }

  &:focus-visible::after {
    opacity: 1;
  }

  &:active {
    transform: scale(${isDisabled ? '1' : '0.9'});
  }
`;

const iconStyles = css`
  fill: currentcolor;
  height: auto;
`;

const defaultIconSize = css`
  width: 1.5rem;
`;

const f1IconSize = css`
  width: 1rem;
`;

const currentValueStyles = css`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  line-height: 1;
  width: 1.5rem;
`;

const defaultValue = css`
  font-size: 1.25rem;
`;

const alternateValue = css`
  font-size: 0.875rem;
  width: 26px;
`;

interface ButtonControlsProps {
  displayValue: string;
  fieldId: string;
  label: string;
  onChange: (operand: 'add' | 'subtract') => () => void;
  toggleConstraints?: {
    add: { isDisabled: boolean };
    subtract: { isDisabled: boolean };
  };
}

const iconSets = {
  alternate: [Minus, Plus],
  default: [MinusCircle, PlusCircle],
  [REVOLUT]: [RevolutMinus, RevolutPlus],
};

const getIcon = (index: number, useAlternateLayout: boolean, isRevolut: boolean) => {
  if (isRevolut) {
    return iconSets[REVOLUT][index];
  }
  return iconSets[useAlternateLayout ? 'alternate' : 'default'][index];
};

const ButtonControls = ({
  displayValue,
  fieldId,
  label,
  onChange,
  toggleConstraints,
}: ButtonControlsProps) => {
  const { isRevolut } = useRevolut();
  const { p } = useProperty();
  const { t } = useTranslation();
  const useAlternateLayout = p('SearchForm.Passengers.AlternateInputLayout.Enabled');

  const inputId = `${fieldId}-passengers-input`;
  const MinusIcon = getIcon(0, useAlternateLayout, isRevolut);
  const PlusIcon = getIcon(1, useAlternateLayout, isRevolut);

  return (
    <Inline className={positioningStyles} spacing={useAlternateLayout ? 16 : 4}>
      <button
        aria-disabled={toggleConstraints?.subtract.isDisabled}
        aria-label={`${t('Air.Search.Subtract')} ${label}`}
        className={cx(
          resetButtonStyle,
          useAlternateLayout
            ? getRevolutButtonStyles(toggleConstraints?.subtract.isDisabled)
            : getButtonStyles(toggleConstraints?.subtract.isDisabled),
        )}
        data-testid={`${fieldId}-passengers-subtract`}
        disabled={toggleConstraints?.subtract.isDisabled}
        onClick={onChange('subtract')}
        type="button"
      >
        <MinusIcon className={cx(iconStyles, isRevolut ? f1IconSize : defaultIconSize)} />
      </button>
      <input
        aria-hidden
        className={screenReadersOnlyStyles}
        id={inputId}
        readOnly
        tabIndex={-1}
        type="number"
        value={displayValue}
      />
      <div
        aria-hidden
        className={cx(currentValueStyles, useAlternateLayout ? alternateValue : defaultValue)}
        data-testid={`${fieldId}-passengers-currentValue`}
      >
        {displayValue}
      </div>
      <button
        aria-disabled={toggleConstraints?.add.isDisabled}
        aria-label={`${t('Air.Search.Add')} ${label}`}
        className={cx(
          resetButtonStyle,
          useAlternateLayout
            ? getRevolutButtonStyles(toggleConstraints?.add.isDisabled)
            : getButtonStyles(toggleConstraints?.add.isDisabled),
        )}
        data-testid={`${fieldId}-passengers-add`}
        disabled={toggleConstraints?.add.isDisabled}
        onClick={onChange('add')}
        type="button"
      >
        <PlusIcon className={cx(iconStyles, isRevolut ? f1IconSize : defaultIconSize)} />
      </button>
    </Inline>
  );
};

export default ButtonControls;
